import {
  Control,
  Controller,
  FieldErrors,
  UseFormClearErrors,
  UseFormGetValues,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';
import {
  decomActions,
  isVaultExportAction,
} from '../../../../../shared/decom-actions';
import {
  GroupHeader,
  GroupItems,
} from '../../../../../shared/autocomplete-groups-ui';
import {
  ActionErrorEntry,
  createDefaultActionData,
} from '../decom-policy-utils.tsx';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import ActionAccordionOld from './actions-accordion-old.tsx';
import DecomAction from './decom-action.tsx';
import VaultIcon from '../../../../../assets/subscriptions/vault.svg';
import { DecomPolicyFormValues } from '../decom-policy-form-layout.tsx';
import { get } from 'react-hook-form';
import { FormSubSteps } from '../../../../../shared/shared-form-stepper.tsx';
import { useUserSettings } from '../../../../../utils/use-user-settings.tsx';

import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import ListItem from '@mui/material/ListItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';
import DateRangeOutlined from '@mui/icons-material/DateRangeOutlined';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';
import EmailOutlined from '@mui/icons-material/EmailOutlined';
import PersonOutlined from '@mui/icons-material/PersonOutlined';
import PhoneIphoneOutlined from '@mui/icons-material/PhoneIphoneOutlined';
import UnfoldLess from '@mui/icons-material/UnfoldLess';

interface DayActionsStepOldProps {
  control: Control<DecomPolicyFormValues>;
  errors: FieldErrors<DecomPolicyFormValues>;
  watch: UseFormWatch<DecomPolicyFormValues>;
  trigger: UseFormTrigger<DecomPolicyFormValues>;
  clearErrors: UseFormClearErrors<DecomPolicyFormValues>;
  setValue: UseFormSetValue<DecomPolicyFormValues>;
  getValues: UseFormGetValues<DecomPolicyFormValues>;
  actionTypesWithErrors: ActionErrorEntry[];
  setActionTypesWithErrors: React.Dispatch<
    React.SetStateAction<ActionErrorEntry[]>
  >;
  day: number;
  setActionSubSteps: Dispatch<SetStateAction<FormSubSteps[]>>;
}

export default function DayActionStepOld({
  control,
  watch,
  errors,
  clearErrors,
  setValue,
  getValues,
  trigger,
  actionTypesWithErrors,
  setActionTypesWithErrors,
  day,
  setActionSubSteps,
}: DayActionsStepOldProps) {
  const theme = useTheme();
  const sharedFormProps = {
    control,
    setValue,
    getValues,
    watch,
    errors,
    clearErrors,
    setActionTypesWithErrors,
    trigger,
    day,
  };

  const { currentTier } = useUserSettings();

  const actionsDataRaw = watch(`actions.${day}.actions`);
  const actionsData = useMemo(() => actionsDataRaw ?? [], [actionsDataRaw]);
  const defaultUserToNotify = watch('defaultUserToNotify') ?? [];
  const defaultNotifyUser = defaultUserToNotify.length > 0 ? true : false;
  const defaultUseManagerEmail = watch('defaultUseManagerEmail') ?? null;
  const defaultDestUser = watch('defaultDestUser') ?? undefined;
  const iconStyle = {
    width: 20,
    height: 20,
    color: theme.palette.primary.main,
  };

  const getActionIcon = (actionType: string): React.ReactNode => {
    if (
      [
        'vaultGmailExport',
        'vaultCalendarExport',
        'vaultChatExport',
        'vaultDriveExport',
        'vaultGeminiExport',
        'vaultGroupsExport',
      ].includes(actionType)
    ) {
      return (
        <img
          src={VaultIcon}
          alt="Vault"
          style={{
            width: iconStyle.width,
            height: iconStyle.height,
          }}
        />
      );
    }

    if (
      [
        'transfercals',
        'ClearPrimaryCalendar',
        'releaseResourceCalendar',
      ].includes(actionType)
    ) {
      return <DateRangeOutlined sx={iconStyle} />;
    }

    if (
      [
        'transferDocs',
        'docsSharedExternallyReport',
        'actSharedExternallyReportMyDrive',
        'actSharedExternallyReportSharedDrive',
        'actSharedPubliclyReportMyDrive',
        'actSharedPubliclyReportSharedDrives',
        'removeExternalShares',
        'RemoveFromAllSharedDrive',
        'removeUserFromShares',
        'removeUsersShares',
      ].includes(actionType)
    ) {
      return <DescriptionOutlined sx={iconStyle} />;
    }

    if (
      [
        'EmailDump',
        'setForwarding',
        'addDelegate',
        'delegateToRelation',
        'removeEmailAliases',
        'removeForwardingAddresses',
      ].includes(actionType)
    ) {
      return <EmailOutlined sx={iconStyle} />;
    }
    if (
      [
        'wipeMobileDevices',
        'wipeMobileAccounts',
        'blockMobileDevices',
        'deleteMobileDevices',
      ].includes(actionType)
    ) {
      return <PhoneIphoneOutlined sx={iconStyle} />;
    }

    if (
      [
        'vacationResponder',
        'sendEmail',
        'ReassignNicknames',
        'UserNameChange',
        'ResetPassword',
        'UserOrgChange',
        'RemoveFromAllGroups',
        'RevokeThirdPartyApps',
        'signOutUser',
        'removeAllLicenses',
        'deleteASPS',
        'deleteSharedContact',
        'deleteUser',
        'suspendUser',
        'unsuspendUser',
        'archiveUser',
        'disableTwoFactor',
        'hideFromDirectory',
      ].includes(actionType)
    ) {
      return <PersonOutlined sx={iconStyle} />;
    }
  };

  const validateUserIsStillInDecomOrg =
    watch('validateUserIsStillInDecomOrg') || false;
  const validateUserIsStillSuspended =
    watch('validateUserIsStillSuspended') || false;

  const [expandedId, setExpandedId] = useState<string[]>([]);

  const selectedActionTypes = actionsData
    .map((a) => a.actionType)
    .filter(Boolean) as string[];

  const handleExpandAll = () => {
    setExpandedId(selectedActionTypes);
  };

  const handleCollapseAll = () => {
    setExpandedId([]);
  };

  const handleToggleExpand = (id: string, isExpanding: boolean) => {
    setExpandedId((prev) =>
      isExpanding ? [...prev, id] : prev.filter((item) => item !== id)
    );
  };

  const { features } = useUserSettings();

  const allSelectedActions =
    Object.values(getValues('actions') ?? {})
      .flatMap((day) => day.actions ?? [])
      .map((a) => a.actionType) ?? [];

  const availableOptions = decomActions.filter(
    (option) =>
      !allSelectedActions.includes(option.value) &&
      !(option.value === 'unsuspendUser' && validateUserIsStillSuspended) &&
      !(option.value === 'UserOrgChange' && validateUserIsStillInDecomOrg) &&
      !(isVaultExportAction(option.value) && !features?.vaultDecomAction) &&
      !(option.value === 'removeSlackChannels' && !features?.slackIntegration)
  );

  const handleRemoveAction = (actionTypeToRemove: string) => {
    const oldIdx = actionsData.findIndex(
      (a) => a.actionType === actionTypeToRemove
    );

    clearErrors(`actions.${day}.actions.${oldIdx}`);

    const newActionsData = actionsData.filter(
      (a) => a.actionType !== actionTypeToRemove
    );

    setValue(`actions.${day}.actions`, newActionsData, { shouldDirty: true });

    setActionTypesWithErrors((prev) =>
      prev.filter((entry) => entry.id !== actionTypeToRemove)
    );

    setActionSubSteps((prev) =>
      prev.filter((value) => {
        const stepIndexLabel =
          day === 0
            ? 'First Run Configuration'
            : day > 1
            ? `${day} Days Out`
            : `${day} Day Out`;
        return !(
          value.id === actionTypeToRemove && value.stepIndex === stepIndexLabel
        );
      })
    );

    setExpandedId((prev) => prev.filter((item) => item !== actionTypeToRemove));
  };

  // For stepper, to add selected actions and set warning if "Needs Attention"
  useEffect(() => {
    setActionSubSteps((prev) => {
      // 1. Filter out all existing actions for this specific day/stepIndex from previous state
      const stepIndexLabel =
        day === 0
          ? 'First Run Configuration'
          : day > 1
          ? `${day} Days Out`
          : `${day} Day Out`;

      const filteredPrev = prev.filter(
        (item) => item.stepIndex !== stepIndexLabel
      );

      // 2. Generate the fresh, current active actions for this day
      const currentDaySubsteps = (actionsData ?? []).map((x) => {
        const actionError = actionTypesWithErrors.find(
          (entry) => entry.id === x.actionType
        );

        return {
          id: x.actionType,
          stepIndex: stepIndexLabel,
          title: decomActions.find((opt) => opt.value === x.actionType)!.label,
          hasError: !!actionError,
        };
      });

      // 3. Combine them to get the complete stepper state across all days
      return [...filteredPrev, ...currentDaySubsteps];
    });
  }, [actionTypesWithErrors, actionsData, day, setActionSubSteps]);

  return (
    <Stack gap={1} flex={1} sx={{ px: 2 }}>
      <Box sx={{ mt: 1, mb: 1, flex: 1, minWidth: 0 }}>
        <Stack
          direction="row"
          gap={1}
          sx={{ width: '100%', alignItems: 'start' }}
        >
          <Controller
            control={control}
            name={`actions.${day}.actions`}
            defaultValue={[]}
            render={() => (
              <Autocomplete
                multiple
                limitTags={2}
                fullWidth
                options={availableOptions.sort((a, b) =>
                  a.groupType.localeCompare(b.groupType)
                )}
                getOptionLabel={(option) => option.label}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Action"
                    error={!!errors.actions?.[day]?.actions}
                    helperText={
                      errors.actions?.[day]?.actions?.message ?? undefined
                    }
                  />
                )}
                groupBy={(option) => option.groupType}
                renderGroup={(params) => (
                  <li key={params.key}>
                    <GroupHeader>{params.group}</GroupHeader>
                    <GroupItems>{params.children}</GroupItems>
                  </li>
                )}
                renderOption={(props, option) => (
                  <ListItem {...props} key={option.value}>
                    {option.label}
                  </ListItem>
                )}
                filterSelectedOptions
                autoComplete
                autoHighlight
                value={actionsData.map(
                  (a) => decomActions.find((opt) => opt.value === a.actionType)!
                )}
                onChange={(_, selectedOptions) => {
                  const selectedActionTypes = selectedOptions.map(
                    (a) => a.value
                  );

                  const existingForDay =
                    getValues(`actions.${day}.actions`) ?? [];

                  const retained = existingForDay.filter((a) =>
                    selectedActionTypes.includes(a.actionType ?? '')
                  );

                  const newOnes = selectedActionTypes
                    .filter(
                      (actionType) =>
                        !existingForDay.some((a) => a.actionType === actionType)
                    )
                    .map((actionType) =>
                      createDefaultActionData(
                        actionType,
                        defaultNotifyUser,
                        defaultUserToNotify,
                        defaultUseManagerEmail,
                        defaultDestUser ?? {
                          userEmail: '',
                          role: 'reader',
                          type: 'user',
                        },
                        currentTier
                      )
                    );

                  const updatedForDay = [...retained, ...newOnes];

                  setValue(`actions.${day}.actions`, updatedForDay, {
                    shouldDirty: true,
                  });

                  newOnes.forEach((action, idxOffset) => {
                    const idx = retained.length + idxOffset;

                    trigger(`actions.${day}.actions.${idx}`).then(() => {
                      const fieldErrors = get(
                        errors as FieldErrors,
                        `actions.${day}.actions.${idx}`
                      );
                      if (fieldErrors) {
                        setActionTypesWithErrors((prev) => {
                          const alreadyExists = prev.some(
                            (e) => e.id === action.actionType
                          );
                          if (!alreadyExists) {
                            return [
                              ...prev,
                              { id: action.actionType, errors: fieldErrors },
                            ];
                          }
                          return prev;
                        });
                      } else {
                        setActionTypesWithErrors((prev) =>
                          prev.filter((e) => e.id !== action.actionType)
                        );
                      }
                      clearErrors(`actions.${day}.actions.${idx}`);
                    });
                  });

                  clearErrors(`actions.${day}`);
                }}
              />
            )}
          />

          <Tooltip arrow title="Collapse/Expand All">
            <IconButton
              sx={{ mt: 0.5 }}
              onClick={
                expandedId.length === actionsData.length
                  ? handleCollapseAll
                  : handleExpandAll
              }
              size="small"
            >
              <UnfoldLess />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      <Box
        sx={{
          width: '100%',
          p: 0.5,
        }}
      >
        <Stack
          gap={1}
          flexDirection="column"
          sx={{
            width: '100%',
            overflowX: 'visible',
          }}
        >
          {selectedActionTypes
            .slice()
            .reverse()
            .map((actionTypeValue) => {
              const action = decomActions.find(
                (a) => a.value === actionTypeValue
              );
              if (!action) return null;
              const { label, description } = action;
              const actionType = actionTypeValue;

              return (
                <ActionAccordionOld
                  id={actionTypeValue}
                  key={actionTypeValue}
                  title={label}
                  icon={getActionIcon(actionTypeValue)}
                  description={description}
                  expanded={expandedId.includes(actionType)}
                  onToggleExpand={(isExpanded) =>
                    handleToggleExpand(actionType, isExpanded)
                  }
                  onRemove={() => handleRemoveAction(actionTypeValue)}
                  {...sharedFormProps}
                  actionTypesWithErrors={actionTypesWithErrors}
                  day={day}
                >
                  <DecomAction
                    {...sharedFormProps}
                    setActionTypesWithErrors={setActionTypesWithErrors}
                    actionType={actionTypeValue}
                    actionDisplayValue={label}
                  />
                </ActionAccordionOld>
              );
            })}
        </Stack>
      </Box>
    </Stack>
  );
}
